import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { refresh } from '../store/authSlice';

/**
 * Custom hook that automatically refreshes user permissions periodically
 * This ensures permissions are always up-to-date without manual logout/login
 */
export const useAutoRefresh = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user, token } = useSelector((state: RootState) => state.auth);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!token || !user) {
      return;
    }

    // Refresh every 2 minutes to keep permissions updated
    const REFRESH_INTERVAL = 120000; // 2 minutes
    
    const refreshUserData = async () => {
      const now = Date.now();
      // Only refresh if at least 30 seconds have passed since last refresh
      if (now - lastRefreshRef.current < 30000) {
        return;
      }

      try {
        lastRefreshRef.current = now;
        const result = await dispatch(refresh());
        if (refresh.fulfilled.match(result)) {
          console.log('🔄 Auto-refreshed user permissions:', result.payload.user.permissions);
        }
      } catch (error) {
        console.error('Failed to auto-refresh user data:', error);
      }
    };

    // Initial refresh after component mounts (with delay to avoid blocking initial load)
    const initialTimeout = setTimeout(() => {
      refreshUserData();
    }, 5000); // Wait 5 seconds after mount

    // Set up periodic refresh
    intervalRef.current = setInterval(refreshUserData, REFRESH_INTERVAL);

    // Cleanup
    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [dispatch, token, user]);
};
